import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  CircularProgress,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Toolbar,
  Button,
  IconButton,
} from "@mui/material";
import { Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import axios from "axios";

const SuperuserBilling = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [selectedBill, setSelectedBill] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      const response = await axios.get("https://therapy-api.accentrixcloud.com/billing");
      setBills(response.data);
      setFilteredBills(response.data);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch bills data");
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredBills(
      bills.filter(
        (bill) =>
          bill.clientName.toLowerCase().includes(value) ||
          bill.email.toLowerCase().includes(value)
      )
    );
  };

  const handleRowClick = (bill) => {
    setSelectedBill(bill);
    setFormData(bill);
    setOpenDetailModal(true);
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    try {
      await axios.put(`https://therapy-api.accentrixcloud.com/billing/${selectedBill.id}`, formData);
      fetchBills();
      setOpenEditModal(false);
    } catch (error) {
      setError("Failed to update bill data");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`https://therapy-api.accentrixcloud.com/billing/${selectedBill.id}`);
      fetchBills();
      setOpenDeleteModal(false);
    } catch (error) {
      setError("Failed to delete bill data");
    }
  };

  const renderTable = () => (
    <TableContainer component={Paper}>
      <Table size={isMobile ? "small" : "medium"}>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Client Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone Number</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredBills.map((bill) => (
            <TableRow key={bill.id} onClick={() => handleRowClick(bill)}>
              <TableCell>{new Date(bill.date).toLocaleString()}</TableCell>
              <TableCell>{bill.clientName}</TableCell>
              <TableCell>{bill.email}</TableCell>
              <TableCell>{bill.phone}</TableCell>
              <TableCell>{bill.amount}</TableCell>
              <TableCell>{bill.status}</TableCell>
              <TableCell>
                <IconButton onClick={() => handleRowClick(bill)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => {
                  setSelectedBill(bill);
                  setOpenDeleteModal(true);
                }}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>Billing</Typography>
      <Toolbar>
        <TextField
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search bills"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Toolbar>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredBills.length > 0 ? (
        renderTable()
      ) : (
        <Typography variant="body1" sx={{ mt: 4 }}>
          No bills found.
        </Typography>
      )}

      <Modal open={openDetailModal} onClose={() => setOpenDetailModal(false)} sx={{ overflowY: "auto" }}>
        <Box component={Paper} sx={{ ...modalStyle, width: isMobile ? '90%' : 400 }}>
          {selectedBill && (
            <>
              <Typography variant="h6" gutterBottom>Bill Details</Typography>
              <Typography variant="body1"><strong>Client Name:</strong> {selectedBill.clientName}</Typography>
              <Typography variant="body1"><strong>Email:</strong> {selectedBill.email}</Typography>
              <Typography variant="body1"><strong>Phone:</strong> {selectedBill.phone}</Typography>
              <Typography variant="body1"><strong>Amount:</strong> {selectedBill.amount}</Typography>
              <Typography variant="body1"><strong>Status:</strong> {selectedBill.status}</Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setOpenDetailModal(false)}
                sx={{ mt: 1 }}
              >
                Close
              </Button>
            </>
          )}
        </Box>
      </Modal>

      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)} sx={{ overflowY: "auto" }}>
        <Box component={Paper} sx={{ ...modalStyle, width: isMobile ? '90%' : 400 }}>
          <Typography variant="h6" gutterBottom>Edit Bill</Typography>
          <Box component="form" onSubmit={handleEditSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="clientName"
              label="Client Name"
              name="clientName"
              value={formData.clientName || ""}
              onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              value={formData.email || ""}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="phone"
              label="Phone Number"
              name="phone"
              value={formData.phone || ""}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="amount"
              label="Amount"
              name="amount"
              value={formData.amount || ""}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="status"
              label="Status"
              name="status"
              value={formData.status || ""}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} sx={{ overflowY: "auto" }}>
        <Box component={Paper} sx={modalStyle}>
          <Typography variant="h6" gutterBottom>Confirm Delete</Typography>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this bill?
          </Typography>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleDelete}
            sx={{ mt: 2 }}
          >
            Delete
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setOpenDeleteModal(false)}
            sx={{ mt: 1 }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>

      <Modal open={Boolean(error)} onClose={() => setError("")}>
        <Box sx={modalStyle}>
          <Typography variant="h6" gutterBottom>
            Error
          </Typography>
          <Typography variant="body1">
            {error}
          </Typography>
          <Button onClick={() => setError("")} color="primary">
            OK
          </Button>
        </Box>
      </Modal>
    </Container>
  );
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflowY: "auto",
};

export default SuperuserBilling;